import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PostCard from "@/components/community/PostCard";
import { TAG_OPTIONS } from "@/data/forge";

const TABS = [
  { id: "creation", label: "Creations", blurb: "Dice and Pouches built by Forgers. Rebuild them in the app." },
  { id: "challenge", label: "Challenges", blurb: "Multi-day plans and challenges. Mark yourself in and report back." },
  { id: "feedback", label: "Feedback", blurb: "Bugs, ideas and tweaks. Upvote what matters, watch the status." },
];

export default function Community() {
  const [params, setParams] = useSearchParams();
  const kind = params.get("kind") || "creation";
  const sort = params.get("sort") || "new";
  const tag = params.get("tag") || "";
  const q = params.get("q") || "";
  const [query, setQuery] = useState(q);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/posts", {
        params: { kind, sort, tag: tag || undefined, q: q || undefined },
      });
      setPosts(data);
    } finally {
      setLoading(false);
    }
  }, [kind, sort, tag, q]);

  useEffect(() => {
    load();
  }, [load]);

  const setParam = (key, value) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next);
  };

  useEffect(() => {
    if (query === q) return;
    const t = setTimeout(() => setParam("q", query.trim()), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const activeTab = TABS.find((t) => t.id === kind) || TABS[0];

  return (
    <main data-testid="community-page" className="min-h-screen pt-28 pb-24">
      <div className="mx-auto w-[min(1080px,calc(100%-32px))]">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="eyebrow-label mb-3">The Forge community</div>
            <h1 className="headline text-4xl sm:text-5xl lg:text-6xl">
              <span className="text-white">Share what </span>
              <span className="text-forge">you forge</span>
            </h1>
          </div>
          <button
            data-testid="new-post-btn"
            onClick={() => navigate(user ? `/community/new?kind=${kind}` : "/login")}
            className="display text-[11px] tracking-[0.25em] px-6 py-3.5 rounded-full bg-forge text-white hover:bg-ember transition-colors"
          >
            + New post
          </button>
        </div>

        <div className="flex gap-2 mb-6 flex-wrap">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              data-testid={`community-tab-${tab.id}`}
              onClick={() => setParam("kind", tab.id)}
              className={`display text-[11px] tracking-[0.2em] px-5 py-3 rounded-full border transition-colors ${
                kind === tab.id
                  ? "border-forge text-forge bg-forge/10"
                  : "border-white/10 text-white/45 hover:text-white"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <p className="text-sm text-white/40 mb-8">{activeTab.blurb}</p>

        <div className="flex flex-wrap items-center gap-3 mb-8">
          <div className="relative flex-1 min-w-[220px]">
            <svg
              viewBox="0 0 24 24"
              className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M20 20l-4.3-4.3" strokeLinecap="round" />
            </svg>
            <input
              data-testid="community-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={kind === "feedback" ? "Search feedback…" : "Search dice, Pouches, tags…"}
              className="w-full text-sm bg-white/[0.04] border border-white/10 rounded-full pl-11 pr-10 py-2.5 text-white placeholder:text-white/30 focus:outline-none focus:border-ember transition-colors"
            />
            {query && (
              <button
                data-testid="community-search-clear"
                aria-label="Clear search"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-white/35 hover:text-ember transition-colors"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M6 6l12 12M18 6L6 18" strokeLinecap="round" />
                </svg>
              </button>
            )}
          </div>
          <div className="flex gap-2">
            {[
              { id: "new", label: "Newest" },
              { id: "top", label: kind === "feedback" ? "Most voted" : "Most liked" },
            ].map((s) => (
              <button
                key={s.id}
                data-testid={`sort-${s.id}`}
                onClick={() => setParam("sort", s.id)}
                className={`text-xs px-4 py-2 rounded-full border transition-colors ${
                  sort === s.id ? "border-ember text-ember" : "border-white/10 text-white/40 hover:text-white"
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          {kind !== "feedback" && (
            <select
              data-testid="tag-filter"
              value={tag}
              onChange={(e) => setParam("tag", e.target.value)}
              className="text-xs bg-white/[0.04] border border-white/10 rounded-full px-4 py-2 text-white/60 focus:outline-none focus:border-ember"
            >
              <option value="">All tags</option>
              {TAG_OPTIONS.map((t) => (
                <option key={t} value={t}>
                  #{t}
                </option>
              ))}
            </select>
          )}
        </div>

        {!user && (
          <div className="panel rounded-2xl p-5 mb-8 text-sm text-white/50">
            Reading is open to everyone.{" "}
            <Link to="/login" data-testid="community-signin-link" className="text-ember hover:text-forge">
              Sign in
            </Link>{" "}
            to post, comment and vote.
          </div>
        )}

        {loading ? (
          <div className="text-white/30 text-sm py-16 text-center">Loading the forge…</div>
        ) : posts.length === 0 ? (
          <div data-testid="empty-state" className="text-center py-20">
            <p className="headline text-3xl mb-3">Nothing here yet.</p>
            <p className="text-sm text-white/40">
              {q ? (
                <>
                  No {activeTab.label.toLowerCase()} match “{q}”.{" "}
                  <button
                    data-testid="community-search-reset"
                    onClick={() => setQuery("")}
                    className="text-ember hover:text-forge"
                  >
                    Clear the search
                  </button>
                </>
              ) : (
                <>Be the first to post in {activeTab.label}.</>
              )}
            </p>
          </div>
        ) : (
          <>
            {q && (
              <p data-testid="search-result-count" className="text-xs text-white/35 mb-4">
                {posts.length} {posts.length === 1 ? "result" : "results"} for “{q}”
              </p>
            )}
            <div className="grid md:grid-cols-2 gap-4" data-testid="post-list">
              {posts.map((post) => (
                <PostCard key={post.post_id} post={post} />
              ))}
            </div>
          </>
        )}
      </div>
    </main>
  );
}
